// src/components/Jobs/JobDetailModal.jsx
import React, { useState } from 'react';
import { getExperienceLabel } from '../../utils/experienceLevels';

const JobDetailModal = ({ job, similarJobs = [], isOpen, onClose, onApply, onViewSimilar }) => {
    const [shareCopied, setShareCopied] = useState(false);

    if (!isOpen || !job) return null;
    
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const getLocationDisplay = () => {
        if (job.city) {
            return `${job.city}, ${job.province}`;
        }
        return job.province || 'Location not specified';
    };

    // Generate shareable URL for this job
    const getShareUrl = () => {
        const baseUrl = window.location.origin;
        return `${baseUrl}/jobs?job=${job.id}`;
    };

    // Copy to clipboard
    const handleCopyLink = async () => {
        const url = getShareUrl();
        try {
            await navigator.clipboard.writeText(url);
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 3000);
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = url;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 3000);
        }
    };

    // Native share (mobile)
    const handleShare = async () => {
        const url = getShareUrl();
        const shareData = {
            title: job.title,
            text: `Check out this job: ${job.title} at ${job.company || 'Company'}!`,
            url: url,
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                // User cancelled or sharing failed
                console.log('Share cancelled:', err);
            }
        } else {
            // Fallback to copy
            handleCopyLink();
        }
    };

    // Share via Email
    const handleShareEmail = () => {
        const subject = encodeURIComponent(`Job: ${job.title}`);
        const body = encodeURIComponent(
            `Hi,\n\nI found this job opportunity and thought you might be interested:\n\n` +
            `${job.title} at ${job.company || 'Company'}\n` +
            `Location: ${getLocationDisplay()}\n` +
            `Work Type: ${job.workType}\n` +
            `Closing Date: ${formatDate(job.closingDate)}\n\n` +
            `View full details: ${getShareUrl()}\n\n` +
            `Regards,\n`
        );
        window.open(`mailto:?subject=${subject}&body=${body}`);
    };

    // Share via WhatsApp
    const handleShareWhatsApp = () => {
        const message = encodeURIComponent(
            `*${job.title}* at ${job.company || 'Company'}\n` +
            `📍 ${getLocationDisplay()}\n` +
            `💼 ${job.workType}\n\n` +
            `View details: ${getShareUrl()}`
        );
        window.open(`https://wa.me/?text=${message}`);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="sticky top-0 float-right m-4 z-10 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-md text-gray-400 hover:text-gray-600 transition-all">
                    <i className="fas fa-times"></i>
                </button>
                
                <div className="p-6 pt-0">
                    <h2 className="text-2xl font-bold text-gradpath-green mb-4 pr-8 pt-6">{job.title}</h2>
                    
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <p className="text-gray-700">
                            <i className="fas fa-building text-gradpath-green mr-2"></i>
                            <strong>Company:</strong> {job.company || "Company Name"}
                        </p>
                        <p className="text-gray-700 mt-1">
                            <i className="fas fa-map-marker-alt text-gradpath-green mr-2"></i>
                            <strong>Location:</strong> {getLocationDisplay()}
                        </p>
                    </div>
                    
                    <div className="flex flex-wrap gap-3 mb-4">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                            <i className="fas fa-briefcase text-gradpath-green"></i> {job.workType}
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600">
                            <i className="fas fa-calendar-alt text-gradpath-green"></i> Closes: {formatDate(job.closingDate)}
                        </span>
                        {job.experienceLevel && (
                            <span className="inline-flex items-center gap-1 text-sm bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                                <i className="fas fa-chart-line"></i>
                                {getExperienceLabel(job.experienceLevel)}
                            </span>
                        )}
                    </div>
                    
                    {job.salary && (
                        <p className="text-gray-700 mb-4">
                            <i className="fas fa-money-bill-wave text-gradpath-green mr-2"></i>
                            <strong>Salary:</strong> {job.salary}
                        </p>
                    )}
                    
                    <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gradpath-dark mb-2">Job Description</h3>
                        <p className="text-gray-600 leading-relaxed whitespace-pre-line">{job.description}</p>
                    </div>
                    
                    {job.requirements && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gradpath-dark mb-2">Requirements</h3>
                            <p className="text-gray-600 leading-relaxed whitespace-pre-line">{job.requirements}</p>
                        </div>
                    )}
                    
                    {job.skills && job.skills.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gradpath-dark mb-2">Skills</h3>
                            <div className="flex flex-wrap gap-2">
                                {job.skills.map((skill, index) => (
                                    <span key={index} className="bg-green-50 text-gradpath-green px-3 py-1 rounded-full text-sm">
                                        {skill}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Action Buttons Row */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={() => onApply(job)}
                            className="flex-1 bg-gradient-to-r from-gradpath-green to-gradpath-dark text-white font-semibold py-3 px-4 rounded-lg hover:shadow-lg transition-all inline-flex items-center justify-center gap-2"
                        >
                            <i className="fas fa-external-link-alt"></i> Apply Now
                        </button>
                        
                        {/* Share Button Dropdown */}
                        <div className="relative">
                            <button
                                onClick={handleShare}
                                className="bg-gray-100 text-gray-700 font-semibold py-3 px-4 rounded-lg hover:bg-gray-200 transition-all inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                            >
                                <i className="fas fa-share-alt"></i> Share
                            </button>
                        </div>
                    </div>

                    {/* Share Options Row */}
                    <div className="mt-3 flex flex-wrap gap-2 justify-end">
                        <button
                            onClick={handleCopyLink}
                            className="text-xs text-gray-500 hover:text-gradpath-green transition-all flex items-center gap-1"
                        >
                            <i className="fas fa-link"></i>
                            {shareCopied ? 'Copied!' : 'Copy Link'}
                        </button>
                        <button
                            onClick={handleShareEmail}
                            className="text-xs text-gray-500 hover:text-gradpath-green transition-all flex items-center gap-1"
                        >
                            <i className="fas fa-envelope"></i> Email
                        </button>
                        <button
                            onClick={handleShareWhatsApp}
                            className="text-xs text-gray-500 hover:text-gradpath-green transition-all flex items-center gap-1"
                        >
                            <i className="fab fa-whatsapp"></i> WhatsApp
                        </button>
                    </div>

                    {similarJobs && similarJobs.length > 0 && (
                        <div className="mt-8 pt-6 border-t border-gray-200">
                            <h3 className="text-lg font-semibold text-gradpath-dark mb-4 flex items-center gap-2">
                                <i className="fas fa-arrows-up-down text-gradpath-green"></i>
                                Similar Jobs You Might Like
                            </h3>
                            <div className="grid gap-4">
                                {similarJobs.map(similarJob => (
                                    <div 
                                        key={similarJob.id}
                                        onClick={() => onViewSimilar(similarJob)}
                                        className="bg-gray-50 rounded-lg p-4 cursor-pointer hover:bg-gray-100 transition group"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h4 className="font-medium text-gradpath-dark group-hover:text-gradpath-green transition">
                                                    {similarJob.title}
                                                </h4>
                                                <p className="text-sm text-gray-500">{similarJob.company}</p>
                                                <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-400">
                                                    <span>{similarJob.province}</span>
                                                    {similarJob.city && <span>• {similarJob.city}</span>}
                                                    <span>• {similarJob.workType}</span>
                                                    {similarJob.experienceLevel && (
                                                        <span>• {getExperienceLabel(similarJob.experienceLevel)}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <button className="text-gradpath-green hover:text-gradpath-dark text-sm font-medium">
                                                View <i className="fas fa-arrow-right ml-1"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default JobDetailModal;